'use client';

/**
 * CanadaLegalSections — the three in-page legal bands from the Canada handoff:
 * the Canadian investor (Belco) disclosure, Terms of Use, and Privacy Policy.
 *
 * These render inline rather than linking out to `/terms` and `/privacy`,
 * because the Canadian terms are governed by the laws of Ontario and the
 * privacy policy is written to PIPEDA — both differ from the US documents at
 * those routes. Copy is verbatim from the handoff; do not paraphrase it.
 */
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import {
  BELCO_EMAIL,
  BELCO_MAILTO,
  CANADA_LEGAL_LAST_UPDATED,
  CONTACT_EMAIL,
  PRIVACY_EMAIL,
} from '../data/canadaContent';

const Band = styled('section', {
  shouldForwardProp: (prop) => prop !== 'tint',
})<{ tint: 'cream' | 'cream2' }>(({ tint }) => ({
  background: tint === 'cream' ? COLORS.cream : COLORS.cream2,
  borderTop: `1px solid ${COLORS.gold}`,
  scrollMarginTop: '84px',
}));

const Head = styled('div')({
  maxWidth: 1240,
  margin: '0 auto',
  padding: '48px var(--pad) 16px',
});

const Eyebrow = styled('span')({
  display: 'block',
  fontFamily: FONTS.sans,
  fontWeight: 600,
  fontSize: '12px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: COLORS.gold,
  marginBottom: '18px',
});

const Title = styled('h2')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '24px',
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  color: COLORS.ink,
  margin: '0 0 10px',
});

const Stamp = styled('p')({
  fontFamily: FONTS.mono,
  fontSize: '12px',
  letterSpacing: '0.06em',
  color: COLORS.ink3,
  margin: 0,
});

const Body = styled('div')({
  maxWidth: 1240,
  margin: '0 auto',
  padding: '0 var(--pad) 40px',
  fontFamily: FONTS.sans,
  fontSize: '13px',
  lineHeight: 1.5,
  color: COLORS.ink3,
  '& p': {
    margin: '0 0 22px',
  },
  '& p:last-child': {
    marginBottom: 0,
  },
  '& h3': {
    fontFamily: FONTS.serif,
    fontWeight: 400,
    fontSize: '15px',
    color: COLORS.ink,
    letterSpacing: 0,
    margin: '20px 0 6px',
  },
  '& ul': {
    margin: '0 0 22px',
    paddingLeft: '22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
  },
  '& a': {
    color: COLORS.tealInk,
    textDecoration: 'none',
    '&:hover': { color: COLORS.gold },
  },
});

export interface CanadaLegalSectionsProps {
  /**
   * Gates the Canadian investor (Belco) disclosure band only — Terms of Use and
   * the Privacy Policy always render, since the footer links to them.
   */
  showBelcoDisclosure?: boolean;
  'data-cy'?: string;
}

export function CanadaLegalSections({
  showBelcoDisclosure = true,
  'data-cy': dataCy = 'canada-legal-sections',
}: CanadaLegalSectionsProps) {
  return (
    <div data-cy={dataCy}>
      {showBelcoDisclosure && (
        <Band tint="cream2" id="canada-belco-disclosure" data-cy="canada-belco-disclosure">
          <Head>
            <Eyebrow>Legal</Eyebrow>
            <Title>Important information for Canadian investors</Title>
          </Head>
          <Body>
            <p>
              Belco Private Capital Inc. has been retained as the portfolio manager, investment fund
              manager and exempt market dealer to provide StartEngine and related and connected
              issuers with all services requiring such registrations in Canada.
            </p>
            <p>
              For offering documents and subscription information, and to discuss whether a
              StartEngine investment available in Canada is suitable for you, please contact{' '}
              <strong>Warren Aarons</strong>, a registered dealing representative with Belco, at{' '}
              <a href={BELCO_MAILTO}>{BELCO_EMAIL}</a>.
            </p>
            <p>
              Under no circumstances should this information be construed as an offering of any
              securities for sale directly or indirectly in any province or territory of Canada. Any
              offering is made only pursuant to the relevant offering documents together with the
              relevant subscription agreement, both of which should be read in their entirety. No
              offer to sell securities will be made prior to receipt of these documents by the
              offeree, and no offer to purchase securities will be accepted prior to completion of
              all appropriate documentation. Neither the Ontario Securities Commission nor any other
              securities regulatory authority of any Canadian jurisdiction has passed upon the
              accuracy or adequacy of this information material, and any representation to the
              contrary is unlawful. Results may differ materially from those described herein, and
              there may be factors and risks that cause actions, events or results not to be as
              anticipated, estimated or intended. You should not place undue reliance on past
              performance or forward-looking information contained herein.
            </p>
          </Body>
        </Band>
      )}

      <Band tint="cream" id="canada-terms" data-cy="canada-terms">
        <Head>
          <Eyebrow>Legal</Eyebrow>
          <Title>Terms of Use</Title>
          <Stamp>{CANADA_LEGAL_LAST_UPDATED} · Governed by the laws of Ontario, Canada</Stamp>
        </Head>
        <Body>
          <p>
            By accessing this website (the &ldquo;Site&rdquo;), you acknowledge these terms and
            conditions of use and agree to be bound by them. If you do not agree to these terms,
            please discontinue accessing the Site immediately.
          </p>
          <p>
            We reserve the right, in our sole discretion, to modify, alter or otherwise update these
            terms and conditions at any time and you agree to be bound by such modifications,
            alterations or updates.
          </p>
          <h3>Not an offer or solicitation</h3>
          <p>
            This Site is only intended to provide you with general information and is neither an
            offer to sell nor a solicitation of an offer to purchase any security and may not be
            relied upon for investment purposes.
          </p>
          <p>
            Any commentaries and information contained in the Site are provided as a general source
            of information and should not be considered personal investment advice. Every effort has
            been made to ensure that the material contained herein is accurate at the time of
            publication.
          </p>
          <p>
            Information on the Site is not intended to provide legal, accounting, financial or tax
            advice, and should not be relied upon in that regard.
          </p>
          <h3>No warranties</h3>
          <p>
            The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis,
            without any representations, warranties or conditions of any kind, whether expressed or
            implied, and including without limitation implied representations, warranties or
            conditions of title, non-infringement, merchantability, fitness for a particular
            purpose, performance, durability, or availability, all of which are hereby disclaimed by
            StartEngine to the fullest extent permitted by law.
          </p>
          <h3>Limitation of liability</h3>
          <p>
            StartEngine will not under any circumstances be liable to you or any other person in any
            way for any loss or damages (direct, indirect, special, economic, incidental,
            consequential, punitive or exemplary, including without limitation loss of revenue,
            data, anticipated profits or lost business) in respect of use of, or inability to use,
            or any reliance on, the Site, any of its content (including without limitation any
            inaccuracies or viruses), or any web browser or other equipment.
          </p>
          <h3>Indemnity</h3>
          <p>
            By accessing the Site, you agree to indemnify and hold harmless StartEngine and its
            agents, suppliers and their respective directors, officers and employees from and
            against any and all actions, proceedings, costs, claims, liabilities, damages and
            expenses (including reasonable legal fees) arising from or in connection with a breach
            of these terms and conditions or the use of this Site.
          </p>
          <h3>Third-party links</h3>
          <p>
            StartEngine may provide links for your convenience to websites produced by other
            providers or industry related material. Accessing websites through links directs you
            away from our website. StartEngine is not responsible for errors or omissions in the
            material on third party websites and does not necessarily approve of or endorse the
            information provided. Users who gain access to third party websites may be subject to
            the copyright and other restrictions on use imposed by those providers and assume
            responsibility and risk from use of those websites.
          </p>
          <h3>Intellectual property</h3>
          <p>
            The contents of the Site are protected by applicable copyright and trademark laws.
            Unauthorized use or exploitation of such content is strictly prohibited including
            without limitation, unauthorized downloading, retransmission, storage in any medium,
            copying, redistribution, reproduction, or republication of the Site, or any part
            thereof, for any purpose. All StartEngine trademarks used on the Site are under license.
          </p>
          <p>
            Links to the Site without the express written permission of StartEngine are strictly
            prohibited. To request permission, please contact{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. The framing, mirroring,
            scraping or data mining of the Site or any of its content in any form and by any method
            is strictly prohibited.
          </p>
          <h3>Electronic transmission</h3>
          <p>
            StartEngine cannot guarantee complete confidentiality or security for information that
            is transmitted electronically. By accessing the Site, you acknowledge that StartEngine
            is not responsible for any damages or losses you may suffer as a result of your
            electronic transmission of confidential or sensitive information to us.
          </p>
          <h3>Severability</h3>
          <p>
            If any provision of these terms and conditions is found to be unlawful, void, or for any
            reason unenforceable, then that provision shall be deemed to be severed from the rest of
            these terms and conditions and shall not affect the validity and enforceability of any
            remaining provisions.
          </p>
          <h3>Governing law</h3>
          <p>
            These terms and conditions are governed by the laws of Ontario and the laws of Canada
            applicable therein, excluding any conflict of laws which would lead to the application
            of any other laws. By accessing the Site, you hereby irrevocably submit and attorn to
            the provincial and federal courts located in the City of Toronto, in the Province of
            Ontario for any disputes or matters arising from, connected with, or relating to the
            Site, this agreement or any related matters, to the extent permitted by applicable law.
          </p>
        </Body>
      </Band>

      <Band tint="cream2" id="canada-privacy" data-cy="canada-privacy">
        <Head>
          <Eyebrow>Legal</Eyebrow>
          <Title>Privacy Policy</Title>
          <Stamp>{CANADA_LEGAL_LAST_UPDATED}</Stamp>
        </Head>
        <Body>
          <p>
            StartEngine and, in Canada, Belco Private Capital Inc. (&ldquo;Belco&rdquo;) are
            committed to protecting the privacy of your personal information. This Privacy Policy
            explains what personal information we collect, how we use and disclose it, and the
            choices available to you. It is intended to comply with the{' '}
            <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and
            applicable provincial privacy laws.
          </p>
          <h3>Information we collect</h3>
          <p>
            We collect personal information that you provide to us and that is generated through
            your use of this website (the &ldquo;Site&rdquo;), including:
          </p>
          <ul>
            <li>
              Identity and contact details, such as your name, email address, mailing address, and
              telephone number;
            </li>
            <li>
              Investor qualification and suitability information required to assess eligibility
              under prospectus exemptions;
            </li>
            <li>Financial information necessary to process a subscription, where applicable;</li>
            <li>Account credentials and preferences; and</li>
            <li>
              Technical and usage data collected automatically, such as IP address, device and
              browser information, and pages viewed.
            </li>
          </ul>
          <h3>How we use your information</h3>
          <p>We use personal information to:</p>
          <ul>
            <li>Provide, operate, and improve the Site and our services;</li>
            <li>Assess your eligibility and the suitability of an investment for you;</li>
            <li>Process subscriptions and administer investments;</li>
            <li>
              Communicate with you, including responding to inquiries and providing offering
              documents;
            </li>
            <li>Comply with legal, regulatory, and record-keeping obligations; and</li>
            <li>Detect, prevent, and address fraud, security, and technical issues.</li>
          </ul>
          <h3>Consent</h3>
          <p>
            We collect, use, and disclose your personal information with your consent, except where
            otherwise permitted or required by law. You may withdraw your consent at any time,
            subject to legal or contractual restrictions and reasonable notice, by contacting our
            Privacy Officer. Withdrawing consent may limit our ability to provide certain services
            to you.
          </p>
          <h3>Disclosure of information</h3>
          <p>We do not sell your personal information. We may share personal information with:</p>
          <ul>
            <li>
              Belco Private Capital Inc., in its capacity as investment fund manager, portfolio
              manager, and exempt market dealer in Canada;
            </li>
            <li>
              Service providers who perform functions on our behalf (such as technology, hosting,
              identity verification, and payment processing), subject to appropriate safeguards;
            </li>
            <li>
              Regulators, law enforcement, or other parties where required or permitted by
              applicable law; and
            </li>
            <li>
              Parties to a business transaction, such as a merger or reorganization, subject to
              applicable law.
            </li>
          </ul>
          <h3>Cross-border transfers</h3>
          <p>
            Some of our service providers and affiliates, including members of the StartEngine group
            of companies, are located in the United States or other jurisdictions. Where personal
            information is processed outside of Canada, it may be subject to the laws of that
            jurisdiction, including lawful access by courts, law enforcement, and regulatory
            authorities.
          </p>
          <h3>Cookies and analytics</h3>
          <p>
            The Site uses cookies and similar technologies to operate the Site, remember your
            preferences, and understand how the Site is used. You can adjust your browser settings
            to refuse cookies, although some features of the Site may not function properly as a
            result.
          </p>
          <h3>Retention and safeguards</h3>
          <p>
            We retain personal information only for as long as necessary to fulfill the purposes for
            which it was collected and to meet legal and regulatory record-keeping requirements. We
            maintain physical, organizational, and technological safeguards designed to protect
            personal information against loss, theft, and unauthorized access, disclosure, or use.
          </p>
          <h3>Your rights</h3>
          <p>
            Subject to applicable law, you may request access to the personal information we hold
            about you, ask that it be corrected or updated, or make a complaint about our handling
            of your personal information. We will respond to your request within the timeframes
            required by applicable law.
          </p>
          <h3>Changes to this policy</h3>
          <p>
            We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
            above indicates when it was last revised. Your continued use of the Site after any
            changes constitutes acceptance of the updated policy.
          </p>
          <h3>Contact us</h3>
          <p>
            To exercise your rights, ask questions, or make a complaint about privacy, please
            contact our Privacy Officer at <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
            For matters relating to investments available in Canada, you may also contact Warren
            Aarons, a registered dealing representative with Belco Private Capital Inc., at{' '}
            <a href={BELCO_MAILTO}>{BELCO_EMAIL}</a>.
          </p>
        </Body>
      </Band>
    </div>
  );
}

export default CanadaLegalSections;
